/**
 * Layout class bundles — page shells, content containers, headers, footers.
 * Complements surface/typography utilities in styles.ts.
 */

/** Main content area background (matches PageContent shell) */
export const layoutPage = "bg-layout";

/** Centered page content wrapper with standard padding */
export const layoutContent =
  "px-4 sm:px-8 pt-6 sm:pt-12 pb-20 flex justify-center";

/** Max-width content column */
export const layoutContainer = "w-full max-w-[1370px]";

/** Full-bleed map/planner content (no max-width, minimal padding) */
export const layoutContentFull = "pt-4";

/** Detail/split panel header row (title + actions) */
export const detailHeader =
  "xl:flex justify-between items-center gap-4 pt-4 mb-4";

/** Detail header without bottom margin */
export const detailHeaderCompact =
  "xl:flex justify-between items-center gap-4 pt-4";

/** Form/dialog action footer */
export const formFooter =
  "flex justify-end gap-4 pt-4 border-t border-border";

/** Sticky bottom action bar (order summary, wizards) */
export const stickyActionBar =
  "flex justify-end gap-3 pt-3 border-t border-border bg-layout w-full py-3 absolute bottom-0 left-0 right-0 px-5";

/** Page title typography */
export const textPageTitle =
  "text-xl font-semibold text-text sm:text-2xl";

/** Page description under title */
export const textPageDescription =
  "text-secondary text-base pt-1 pb-2";

/** Section heading (route, cargo, etc.) */
export const textSectionTitle = "text-lg font-medium text-text";

/** Large section heading (images, etc.) */
export const textSectionTitleLg = "text-2xl font-medium text-text";

/** Detail panel primary title */
export const textDetailTitle = "text-2xl font-medium text-text";

/* -------------------------------------------------------------------------- */
/* Layout primitive scales (Stack / Inline)                                   */
/*                                                                            */
/* These map prop values to STATIC Tailwind class strings so the JIT          */
/* compiler keeps them (never build `gap-${n}` dynamically — it gets purged). */
/* -------------------------------------------------------------------------- */

/** Spacing scale for Stack / Inline gaps (maps to Tailwind `gap-*`). */
export type Gap = 0 | 1 | 1.5 | 2 | 2.5 | 3 | 4 | 5 | 6 | 8;

export const gapClass: Record<Gap, string> = {
  0: "gap-0",
  1: "gap-1",
  1.5: "gap-1.5",
  2: "gap-2",
  2.5: "gap-2.5",
  3: "gap-3",
  4: "gap-4",
  5: "gap-5",
  6: "gap-6",
  8: "gap-8",
};

/** Cross-axis alignment (maps to Tailwind `items-*`). */
export type Align = "start" | "center" | "end" | "baseline" | "stretch";

export const alignClass: Record<Align, string> = {
  start: "items-start",
  center: "items-center",
  end: "items-end",
  baseline: "items-baseline",
  stretch: "items-stretch",
};

/** Main-axis distribution (maps to Tailwind `justify-*`). */
export type Justify = "start" | "center" | "end" | "between" | "around";

export const justifyClass: Record<Justify, string> = {
  start: "justify-start",
  center: "justify-center",
  end: "justify-end",
  between: "justify-between",
  around: "justify-around",
};
